import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Calendar, ChevronDown, Instagram, Facebook, Youtube } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { resolveImage } from "@/lib/api";

const updatedServiceLinks = [
  { title: "Videonasofibroscopia", link: "/servicos/videonasofibroscopia" },
  { title: "Videolaringoscopia", link: "/servicos/videolaringoscopia" },
  { title: "Videoestroboscopia", link: "/servicos/videoestroboscopia" },
  { title: "Tomografias", link: "/servicos/tomografias" },
  { title: "Raio X", link: "/servicos/raio-x" },
  { title: "Audiometria e BERA", link: "/servicos/audiometria-e-bera" },
  { title: "Exames de Otoneurologia", link: "/servicos/exames-de-otoneurologia" },
  { title: "Exames para Labirintopatias", link: "/servicos/exames-para-labirintopatias" },
  { title: "Imunoterapia e Testes Alérgicos", link: "/servicos/imunoterapia-testes-alergicos" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { settings } = useSettings();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const wa = settings?.contact?.whatsapp || "5565996516708";
  const phone = settings?.contact?.phone || "(65) 99651-6708";
  const email = settings?.contact?.email || "";
  const logoImg = settings?.site?.logo;
  const serviceLinks = updatedServiceLinks;

  const socialLinks = [
    { label: "Instagram", href: settings?.contact?.instagram, icon: Instagram },
    { label: "Facebook", href: settings?.contact?.facebook, icon: Facebook },
    { label: "YouTube", href: settings?.contact?.youtube, icon: Youtube },
  ].filter((item) => item.href);

  const links = [
    { to: "/", label: "Início" },
    { to: "/sobre", label: "Sobre" },
    { to: "/cirurgias", label: "Cirurgias" },
    { to: "/servicos", label: "Serviços", hasDropdown: true },
    { to: "/convenios", label: "Convênios" },
    { to: "/faq", label: "Perguntas Frequentes" },
    { to: "/contato", label: "Contato" },
  ];

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-navbar">
      {/* Top info bar */}
      <div className={`hidden md:block transition-all duration-500 overflow-hidden ${scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"}`}>
        <div className="bg-primary text-primary-foreground">
          <div className="container-pad flex items-center justify-between py-2 text-xs">
            <div className="flex items-center gap-6 text-primary-foreground/85">
              <a href={`tel:+55${wa}`} className="inline-flex items-center gap-2 hover:text-accent transition" data-testid="topbar-phone">
                <Phone className="w-3.5 h-3.5" /> {phone}
              </a>
              {email && <a href={`mailto:${email}`} className="hidden lg:inline-flex items-center gap-2 hover:text-accent transition">
                <Mail className="w-3.5 h-3.5" /> {email}
              </a>}
              <span className="hidden xl:inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Cuiabá - MT</span>
            </div>
            <div className="flex items-center gap-4 text-primary-foreground/85">
              <span>Seg-Sex • 07h às 17h</span>
              <span className="text-accent">Médico Especialista Otorrinolaringologista CRM 10.467 RQE 6533</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className={`transition-all duration-500 ${scrolled ? "bg-white/95 backdrop-blur-xl shadow-md" : "bg-gradient-to-b from-black/30 to-transparent"}`}>
        <div className="container-pad flex items-center justify-between py-3 md:py-4">
          <div className="flex items-center gap-3 md:gap-4">
            <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
              {logoImg ? (
                <img src={resolveImage(logoImg)} alt="logo" className="h-10 md:h-12 w-auto" />
              ) : (
                <div className="flex items-center gap-3">
                  <div className={`relative h-11 w-11 rounded-xl flex items-center justify-center font-display font-semibold text-base shadow-md transition-colors ${scrolled ? "bg-gradient-to-br from-primary to-accent text-white" : "bg-white text-primary"}`}>
                    AN
                  </div>
                  <div className="leading-tight hidden sm:block">
                    <div className={`font-display text-base md:text-lg font-semibold transition-colors ${scrolled ? "text-primary" : "text-white"}`}>Dr. Alonso Neto</div>
                    <div className={`text-[10px] tracking-[0.2em] uppercase transition-colors ${scrolled ? "text-accent" : "text-white/80"}`}>Otorrinolaringologista</div>
                  </div>
                </div>
              )}
            </Link>

            {socialLinks.length > 0 && (
              <div className="hidden sm:flex items-center gap-1.5" data-testid="navbar-social-links">
                {socialLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={item.label}
                      className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all hover:-translate-y-0.5 ${scrolled ? "border-border text-primary hover:text-accent hover:border-accent bg-white/70" : "border-white/25 text-white hover:text-accent hover:border-accent bg-black/10"}`}
                      data-testid={`social-${item.label.toLowerCase()}`}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <nav className="hidden xl:flex items-center gap-6">
            {links.map((l) => {
              if (l.external) {
                return (
                  <a key={l.label} href={l.href} target="_blank" rel="noreferrer" className={`text-[13px] font-medium tracking-wide transition-colors ${scrolled ? "text-foreground/75 hover:text-accent" : "text-white/90 hover:text-accent"}`} data-testid={`nav-link-${l.label.toLowerCase()}`}>
                    {l.label}
                  </a>
                );
              }
              if (l.hasDropdown) {
                return (
                  <div
                    key={l.to}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <NavLink
                      to={l.to}
                      className={({ isActive }) => `inline-flex items-center gap-1 text-[13px] font-medium tracking-wide transition-colors ${scrolled ? (isActive ? "text-accent" : "text-foreground/75 hover:text-accent") : (isActive ? "text-accent" : "text-white/90 hover:text-accent")}`}
                      data-testid="nav-link-servicos"
                    >
                      {l.label} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </NavLink>
                    {servicesOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-72" data-testid="services-dropdown">
                        <div className="bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
                          <div className="p-2 max-h-[70vh] overflow-y-auto">
                            <Link to="/servicos" className="block px-4 py-2.5 rounded-lg text-xs uppercase tracking-widest text-accent font-semibold hover:bg-secondary/40">Ver todos os serviços →</Link>
                            <div className="h-px bg-border my-1" />
                            {serviceLinks.map((sl, i) => {
                              const internal = (sl.link || "").startsWith("/");
                              return internal ? (
                                <Link
                                  key={i}
                                  to={sl.link}
                                  className="block px-4 py-2.5 rounded-lg text-sm text-foreground/80 hover:bg-secondary/40 hover:text-accent transition"
                                  data-testid={`services-dropdown-item-${i}`}
                                >
                                  {sl.title}
                                </Link>
                              ) : (
                                <a
                                  key={i}
                                  href={sl.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="block px-4 py-2.5 rounded-lg text-sm text-foreground/80 hover:bg-secondary/40 hover:text-accent transition"
                                  data-testid={`services-dropdown-item-${i}`}
                                >
                                  {sl.title}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) => `relative text-[13px] font-medium tracking-wide transition-colors ${scrolled ? (isActive ? "text-accent" : "text-foreground/75 hover:text-accent") : (isActive ? "text-accent" : "text-white/90 hover:text-accent")}`}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              );
            })}
          </nav>

          <a
            href={`https://wa.me/${wa}?text=${encodeURIComponent("Olá, gostaria de mais informações ou agendar uma consulta.")}`}
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-all hover:-translate-y-0.5 shadow-md"
            data-testid="navbar-agendar-btn"
          >
            <Calendar className="w-4 h-4" /> Agendar Consulta
          </a>

          <button
            className={`xl:hidden p-2 transition-colors ${scrolled ? "text-foreground" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-border shadow-xl max-h-[80vh] overflow-y-auto" data-testid="navbar-mobile-menu">
          <div className="container-pad py-5 flex flex-col gap-3">
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-2 pb-2 border-b border-border/60">
                {socialLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <a key={item.label} href={item.href} target="_blank" rel="noreferrer" aria-label={item.label} className="w-9 h-9 rounded-full border border-border text-primary flex items-center justify-center hover:text-accent hover:border-accent transition">
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            )}
            {links.map((l) => {
              if (l.external) {
                return (
                  <a key={l.label} href={l.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="text-sm font-medium text-foreground/80 py-1.5">{l.label}</a>
                );
              }
              if (l.hasDropdown) {
                return (
                  <div key={l.to}>
                    <button onClick={() => setServicesOpen((v) => !v)} className="w-full flex justify-between items-center text-sm font-medium text-foreground/80 py-1.5">
                      {l.label} <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                    </button>
                    {servicesOpen && (
                      <div className="pl-3 mt-1 mb-2 space-y-1 border-l-2 border-accent">
                        <Link to="/servicos" onClick={() => setOpen(false)} className="block py-1.5 pl-3 text-xs uppercase tracking-widest text-accent font-semibold">Ver todos →</Link>
                        {serviceLinks.map((sl, i) => {
                          const internal = (sl.link || "").startsWith("/");
                          return internal ? (
                            <Link key={i} to={sl.link} onClick={() => setOpen(false)} className="block py-1.5 pl-3 text-sm text-foreground/70">{sl.title}</Link>
                          ) : (
                            <a key={i} href={sl.link} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="block py-1.5 pl-3 text-sm text-foreground/70">{sl.title}</a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `text-sm font-medium py-1.5 ${isActive ? "text-accent" : "text-foreground/80"}`}>
                  {l.label}
                </NavLink>
              );
            })}
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent text-accent-foreground font-medium">
              <Calendar className="w-4 h-4" /> Agendar Consulta
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
